import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  LockReset as LockResetIcon 
} from '@mui/icons-material';
import { useUser } from '../context/UserContext';
import { userService, roleService } from '../services/apiServices';
import ErrorDisplay from './ErrorDisplay';
import { sanitizeInput } from '../utils/validationUtils';
import { validatePasswordStrength, PASSWORD_REQUIREMENTS_TEXT } from '../utils/passwordUtils';

const Users = () => {
  const { user: activeUser, hasRole, hasPermission } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id: null,
    username: '',
    email: '',
    password: '',
    roles: []
  });
  const [availableRoles, setAvailableRoles] = useState([]);

  // Check if active user has admin:all role or permission
  const canResetPassword = Boolean(
    hasRole('admin:all') ||
    hasPermission('admin:all') ||
    (activeUser?.roles && activeUser.roles.includes('admin:all')) ||
    (activeUser?.permissions && activeUser.permissions.includes('admin:all'))
  );

  // Reset password states
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetDialogError, setResetDialogError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Create user password visibility and dialog error
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [userDialogError, setUserDialogError] = useState('');

  const handleOpenResetDialog = (userToReset) => {
    setResetTargetUser(userToReset);
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetDialogError('');
    setOpenResetDialog(true);
  };

  const handleCloseResetDialog = () => {
    if (resetLoading) return;
    setOpenResetDialog(false);
    setResetTargetUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setResetDialogError('');
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword) {
      setResetDialogError('New password is required.');
      return;
    }
    const passwordValidation = validatePasswordStrength(newPassword);
    if (!passwordValidation.isValid) {
      setResetDialogError(passwordValidation.message);
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetDialogError('Passwords do not match.');
      return;
    }

    setResetLoading(true);
    setResetDialogError('');

    try {
      await userService.resetPassword(resetTargetUser.id, newPassword);
      setSuccessMessage(`Password for user "${resetTargetUser.username}" was reset successfully.`);
      handleCloseResetDialog();
    } catch (err) {
      let errorMessage = 'Error resetting password. Please try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setResetDialogError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  // Fetch users and roles
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users
        const usersResponse = await userService.getUsers();
        setUsers(usersResponse.data);

        // Fetch roles for the form
        const rolesResponse = await roleService.getRoles();
        setAvailableRoles(rolesResponse.data);
      } catch (err) {
        let errorMessage = 'Error loading data. Please try again later.';
        
        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = 'Session expired. Please log in again.';
            localStorage.removeItem('authToken');
            window.location.href = '/login';
          } else if (err.response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to view users.';
          } else if (err.response.status === 500) {
            // Check if this is a specific access denied error
            if (err.response.data?.message?.includes('Access Denied')) {
              errorMessage = 'Access denied. You do not have permission to view users. This feature requires user management permissions.';
            } else {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.request) {
          errorMessage = 'Network error. Please check your connection.';
        } else {
          errorMessage = err.message || 'An unexpected error occurred.';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      // Editing existing user
      setIsEditing(true);
      setCurrentUser({
        id: user.id,
        username: user.username,
        email: user.email,
        password: '', // Don't show current password
        roles: user.roles ? user.roles.map(role => role.name) : []
      });
    } else {
      // Creating new user
      setIsEditing(false);
      setCurrentUser({
        id: null,
        username: '',
        email: '',
        password: '',
        roles: []
      });
    }
    setShowCreatePassword(false);
    setUserDialogError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setUserDialogError('');
    setShowCreatePassword(false);
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (event) => {
    const {
      target: { value },
    } = event;
    setCurrentUser(prev => ({
      ...prev,
      roles: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleSubmit = async () => {
    setUserDialogError('');
    try {
      if (isEditing) {
        // Update existing user
        const response = await userService.updateUser(currentUser.id, {
          email: currentUser.email,
          roles: currentUser.roles
        });
        setUsers(prev => prev.map(u => u.id === currentUser.id ? response.data : u));
        setSuccessMessage(`User "${currentUser.username}" updated successfully.`);
      } else {
        // Validate password strength for new user
        const passwordValidation = validatePasswordStrength(currentUser.password);
        if (!passwordValidation.isValid) {
          setUserDialogError(passwordValidation.message);
          return;
        }

        // Create new user
        const response = await userService.createUser({
          username: currentUser.username,
          password: currentUser.password,
          email: currentUser.email,
          roles: currentUser.roles
        });
        setUsers(prev => [...prev, response.data]);
        setSuccessMessage(`User "${currentUser.username}" created successfully.`);
      }
      
      handleCloseDialog();
    } catch (err) {
      let errorMessage = 'Error saving user. Please try again.';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        } else if (err.response.status === 403) {
          errorMessage = 'Access denied. You do not have permission to modify users.';
        } else if (err.response.status === 409) {
          errorMessage = err.response.data?.message || 'A user with this username or email already exists.';
        } else if (err.response.status === 500) {
          // Check if this is a specific access denied error
          if (err.response.data?.message?.includes('Access Denied')) {
            errorMessage = 'Access denied. You do not have permission to modify users. This feature requires user management permissions.';
          } else {
            errorMessage = 'Server error. Please try again later.';
          }
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await userService.deleteUser(userId);
        setUsers(prev => prev.filter(u => u.id !== userId));
      } catch (err) {
        let errorMessage = 'Error deleting user. Please try again.';
        
        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = 'Session expired. Please log in again.';
            localStorage.removeItem('authToken');
            window.location.href = '/login';
          } else if (err.response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to delete users.';
          } else if (err.response.status === 404) {
            errorMessage = 'User not found.';
            // Remove user from local state if it was deleted on the server but not reflected here
            setUsers(prev => prev.filter(u => u.id !== userId));
          } else if (err.response.status === 500) {
            // Check if this is a specific access denied error
            if (err.response.data?.message?.includes('Access Denied')) {
              errorMessage = 'Access denied. You do not have permission to delete users. This feature requires user management permissions.';
            } else {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.request) {
          errorMessage = 'Network error. Please check your connection.';
        } else {
          errorMessage = err.message || 'An unexpected error occurred.';
        }
        
        setError(errorMessage);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading && users.length === 0) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Users Management
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ErrorDisplay error={error} onClose={() => setError('')} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Users Management
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add User
        </Button>
      </Box>
      
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{sanitizeInput(user.id || 'N/A')}</TableCell>
                  <TableCell>{sanitizeInput(user.username || 'N/A')}</TableCell>
                  <TableCell>{sanitizeInput(user.email || 'N/A')}</TableCell>
                  <TableCell>
                    {user.roles && user.roles.length > 0 
                      ? sanitizeInput(user.roles.map(role => role.name).join(', ')) 
                      : 'No roles'}
                  </TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit User">
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleOpenDialog(user)}
                        aria-label="edit user"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {canResetPassword && (
                      <Tooltip title="Reset Password">
                        <IconButton 
                          size="small" 
                          color="warning" 
                          onClick={() => handleOpenResetDialog(user)}
                          aria-label="reset password"
                        >
                          <LockResetIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete User">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDelete(user.id)}
                        aria-label="delete user"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog for adding/editing users */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          {userDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {userDialogError}
            </Alert>
          )}
          {!isEditing && (
            <TextField
              autoFocus
              margin="dense"
              name="username"
              label="Username"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(currentUser.username)}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={sanitizeInput(currentUser.email)}
            onChange={handleInputChange}
            required
            sx={{ mb: 2 }}
          />
          {!isEditing && (
            <TextField
              margin="dense"
              name="password"
              label="Password"
              type={showCreatePassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={currentUser.password}
              onChange={handleInputChange}
              required
              helperText={PASSWORD_REQUIREMENTS_TEXT}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowCreatePassword(!showCreatePassword)}
                      edge="end"
                    >
                      {showCreatePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="roles-label">Roles</InputLabel>
            <Select
              labelId="roles-label"
              id="roles-select"
              multiple
              value={currentUser.roles}
              onChange={handleRoleChange}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={sanitizeInput(value)} size="small" />
                  ))}
                </Box>
              )}
            >
              {availableRoles.map((role) => (
                <MenuItem key={role.id} value={role.name}>
                  <Checkbox checked={currentUser.roles.indexOf(role.name) > -1} />
                  <ListItemText primary={sanitizeInput(role.name)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for resetting user password */}
      <Dialog open={openResetDialog} onClose={handleCloseResetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reset Password for {sanitizeInput(resetTargetUser?.username)}
        </DialogTitle>
        <DialogContent>
          {resetDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetDialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new password for user <strong>{sanitizeInput(resetTargetUser?.username)}</strong>.
          </Typography>
          <TextField
            margin="dense"
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={resetLoading}
            helperText={PASSWORD_REQUIREMENTS_TEXT}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={resetLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} disabled={resetLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleResetPasswordSubmit} 
            variant="contained" 
            color="warning"
            disabled={resetLoading}
          >
            {resetLoading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success messages */}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;